import { marketplaceSiteLabel, providerMeta, providerRegistry, type ProviderAdapterMeta } from "@/lib/integrations/registry";

export type MerchantConnectability = "oauth" | "coming_soon";

export interface ConnectorOnboardingCopy {
  provider: string;
  marketplaceLabel: string;
  description: string;
  displayNameLabel: string;
  displayNamePlaceholder: string;
  displayNameHelp: string;
  displayNameRequired: boolean;
  siteLabel: string | null;
  siteHelp: string | null;
  siteRequired: boolean;
  connectActionLabel: string;
  helpText: string;
  connectability: MerchantConnectability;
  showsStoreHash: boolean;
  showsCredentialSecretName: boolean;
  showsAccountStoreId: boolean;
}

export interface ConnectorOnboardingDraft {
  open: boolean;
  provider: string;
  name: string;
  marketplace_site: string;
}

export function merchantConnectability(provider: string): MerchantConnectability {
  if (provider === "ebay" || provider === "bigcommerce") return "oauth";
  return "coming_soon";
}

export function defaultMarketplaceSite(provider: string): string {
  if (provider === "ebay") return "EBAY_AU";
  return "";
}

export function emptyConnectorOnboardingDraft(provider = "ebay"): ConnectorOnboardingDraft {
  return {
    open: true,
    provider,
    name: "",
    marketplace_site: defaultMarketplaceSite(provider),
  };
}

export function applyMarketplaceSelection(current: ConnectorOnboardingDraft, provider: string): ConnectorOnboardingDraft {
  return {
    ...current,
    provider,
    marketplace_site: defaultMarketplaceSite(provider),
  };
}

export function connectorOnboardingCopy(provider: string): ConnectorOnboardingCopy {
  const meta: ProviderAdapterMeta | undefined = providerMeta(provider);
  const label = meta?.label ?? provider;
  const connectability = merchantConnectability(provider);

  if (provider === "ebay") {
    return {
      provider,
      marketplaceLabel: label,
      description: meta?.description ?? "",
      displayNameLabel: "Display name (optional)",
      displayNamePlaceholder: "e.g. Australia shop",
      displayNameHelp: "A friendly name for this eBay seller account. Tradivo fills seller identity after you authorize.",
      displayNameRequired: false,
      siteLabel: "Marketplace site",
      siteHelp: "Choose the eBay site this seller account uses.",
      siteRequired: true,
      connectActionLabel: "Connect eBay account",
      helpText: "You'll sign in to eBay and approve Tradivo. Tokens stay on the server.",
      connectability,
      showsStoreHash: false,
      showsCredentialSecretName: false,
      showsAccountStoreId: false,
    };
  }

  if (provider === "bigcommerce") {
    return {
      provider,
      marketplaceLabel: label,
      description: meta?.description ?? "",
      displayNameLabel: "Display name (optional)",
      displayNamePlaceholder: "e.g. Main store",
      displayNameHelp: "A friendly name for this BigCommerce store. Store identity is captured after you authorize.",
      displayNameRequired: false,
      siteLabel: null,
      siteHelp: null,
      siteRequired: false,
      connectActionLabel: "Connect BigCommerce",
      helpText: "You'll approve Tradivo in BigCommerce. Store identity and tokens are captured securely after authorization.",
      connectability,
      showsStoreHash: false,
      showsCredentialSecretName: false,
      showsAccountStoreId: false,
    };
  }

  if (provider === "amazon") {
    return {
      provider,
      marketplaceLabel: label,
      description: meta?.description ?? "Amazon Seller Central authorization will use the same Connect → authorize → return model.",
      displayNameLabel: "Display name (optional)",
      displayNamePlaceholder: "e.g. Amazon AU",
      displayNameHelp: "Amazon connection is not enabled for merchants yet.",
      displayNameRequired: false,
      siteLabel: "Marketplace",
      siteHelp: "Amazon marketplaces will be selected here when this channel is enabled.",
      siteRequired: false,
      connectActionLabel: "Connect Amazon",
      helpText: "Amazon seller authorization is not available in this release. The shared connector flow is the same as eBay and BigCommerce.",
      connectability: "coming_soon",
      showsStoreHash: false,
      showsCredentialSecretName: false,
      showsAccountStoreId: false,
    };
  }

  return {
    provider,
    marketplaceLabel: label,
    description: meta?.description ?? "",
    displayNameLabel: "Display name (optional)",
    displayNamePlaceholder: `${label} store`,
    displayNameHelp: "This marketplace is not available for self-service connection yet.",
    displayNameRequired: false,
    siteLabel: null,
    siteHelp: null,
    siteRequired: false,
    connectActionLabel: `Connect ${label}`,
    helpText: "Coming soon. Tradivo will use marketplace login, then return here as Connected.",
    connectability: "coming_soon",
    showsStoreHash: false,
    showsCredentialSecretName: false,
    showsAccountStoreId: false,
  };
}

export function defaultConnectorDisplayName(provider: string, marketplaceSite: string, typedName: string): string {
  const trimmed = typedName.trim();
  if (trimmed) return trimmed;
  if (provider === "ebay") {
    const site = marketplaceSiteLabel(marketplaceSite) || marketplaceSite;
    return site ? `eBay · ${site}` : "eBay account";
  }
  if (provider === "bigcommerce") return "BigCommerce store";
  const meta = providerMeta(provider);
  return meta ? `${meta.label} account` : "Marketplace account";
}

export function validateConnectorOnboarding(draft: ConnectorOnboardingDraft): string | null {
  const copy = connectorOnboardingCopy(draft.provider);
  if (copy.connectability === "coming_soon") {
    return `${copy.marketplaceLabel} self-service connection is not available yet.`;
  }
  if (copy.siteRequired && !draft.marketplace_site.trim()) {
    return "Choose a marketplace site.";
  }
  return null;
}

export const MERCHANT_FORBIDDEN_UX_STRINGS = [
  "EBAY_STORE_A_CREDENTIALS",
  "BIGCOMMERCE_BORRAQ_CREDENTIALS",
  "Credential secret name",
  "server-side",
  "store hash",
  "seller username or store hash",
] as const;

export function merchantOnboardingProviders(): ProviderAdapterMeta[] {
  return providerRegistry.filter((p) => p.provider === "ebay" || p.provider === "bigcommerce" || p.provider === "amazon" || p.provider === "shopify");
}

export function oauthFunctionName(provider: string): "ebay-oauth-initiate" | "bigcommerce-oauth-initiate" | null {
  if (provider === "ebay") return "ebay-oauth-initiate";
  if (provider === "bigcommerce") return "bigcommerce-oauth-initiate";
  return null;
}
